import React, { useState, useEffect, memo } from "react";
import Lightbox from "./Lightbox";
import OptimizedImage, { OptimizedImageSize } from "./OptimizedImage";

interface ImageThumbnailsWithModalProps {
  imageKeys: string[];
  getImageUrlFromImageKey: (imageKey: string) => Promise<URL>;
  getOptimizedImageSrc: (
    imageKey: string,
    imageSize: OptimizedImageSize,
  ) => Promise<string>;
  imageContainerClassName?: string;
  imageClassName?: string;
}

export const ImageThumbnailsWithModal = memo(
  ({
    imageKeys,
    getImageUrlFromImageKey,
    getOptimizedImageSrc,
    imageContainerClassName,
    imageClassName,
  }: ImageThumbnailsWithModalProps) => {
    const [imageUrls, setImageUrls] = useState<URL[] | null>(null);
    const [currentShownImageIndex, setCurrentShownImageIndex] = useState<
      number | null
    >(null);

    useEffect(() => {
      Promise.all(
        imageKeys.map((key) => {
          return getImageUrlFromImageKey(key);
        }),
      ).then((urls) => {
        setImageUrls(urls);
      });
    }, [imageKeys, getImageUrlFromImageKey]);

    if (imageKeys.length === 0) {
      return null;
    }

    return (
      <>
        <Lightbox
          isVisible={currentShownImageIndex !== null}
          src={imageUrls?.[currentShownImageIndex ?? -1]?.toString() ?? ""}
          alt={imageKeys[currentShownImageIndex ?? -1]}
          close={() => setCurrentShownImageIndex(null)}
          goToPreviousImage={() => {
            if (currentShownImageIndex !== null) {
              setCurrentShownImageIndex(
                (currentShownImageIndex - 1 + imageKeys.length) %
                  imageKeys.length,
              );
            }
          }}
          goToNextImage={() => {
            if (currentShownImageIndex !== null) {
              setCurrentShownImageIndex(
                (currentShownImageIndex + 1) % imageKeys.length,
              );
            }
          }}
        />
        {imageKeys.map((key, idx) => (
          <div className={imageContainerClassName} key={key}>
            <OptimizedImage
              src={key}
              imageSize={512}
              getOptimizedSrc={getOptimizedImageSrc}
              className={imageClassName}
              onClick={() => setCurrentShownImageIndex(idx)}
            />
          </div>
        ))}
      </>
    );
  },
);
